/*
 * @Author: Yolo
 * @Date: 2024-07-02 09:10:08
 * @Last Modified by: Yolo
 * @Last Modified time: 2024-07-02 09:12:01
 * @Desc 中国领海基线
 */
import * as L from 'leaflet'
import { ShipxyOptions } from '@map-sdk/sdk/config'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { CommUtils } from '@map-sdk/sdk/utils/CommUtils'
import { AreaViewBase } from '@map-sdk/sdk/plugins/AreaView/AreaViewBase'
import { baseLine } from '@map-sdk/sdk/plugins/AreaView/layer/BaselinesAtSeaSymbolData'
import type { AreaViewBaseOptions } from '@map-sdk/sdk/plugins/AreaView/AreaViewBase'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'

interface BaselineOptions extends L.PolylineOptions {
  colors?: string[]
  data?: {
    name?: string
    points: { lat: number; lng: number }[]
    options?: {
      minZoom?: number
    }
  }
}

interface BaselinePopupOptions {
  className: string
  closeButton?: boolean
  offset?: [number, number]
}

class BaselinesAtSeaSymbol extends AreaViewBase {
  constructor(map: MyMap, options?: Partial<AreaViewBaseOptions>) {
    super(map, options)
    this._data = baseLine
  }
  _show(): this {
    const defaultOption: L.PolylineOptions = {
      dashArray: [5, 5],
      weight: 2,
    }

    if (!this._data) {
      console.error('区域图形展示，请求无数据！')
      return this
    }

    this.options.colors ||= ['#CF0072', '#FFFF33']
    let colors: string = this.options.colors![0]
    let boundary0: string = ShipxyOptions.COM_IMG_BASE64.boundary_0
    let sty = 'baseline-popup cfff'

    if ('MT_SATELLITE' === this._map!.mapType) {
      colors = this.options.colors![1]
      boundary0 = ShipxyOptions.COM_IMG_BASE64.boundary_f
      sty = 'baseline-popup c000'
    }

    for (let i = 0; i < this._data!.length; i++) {
      const points = this._data![i].points
      const pointsList: L.LatLngExpression[] = []

      for (let j = 0; j < points.length; j++) {
        const point = points[j]
        const latLng = CanvasShipUtils.GPSEncryptByMapToLatLng(
          L.latLng(point.lat, point.lng),
          this._map!
        )
        if (!CommUtils.isEmpty(point.name)) {
          if (
            !(
              this._data[i].options &&
              this._data[i].options.minZoom &&
              this._map!.getZoom() < this._data[i].options.minZoom
            )
          ) {
            let max = 0
            if (j > 0) {
              const latLngToLayerPoint = this._map!.latLngToLayerPoint(
                pointsList[pointsList.length - 1]
              )
              const pointLayerPoint = this._map!.latLngToLayerPoint(latLng)
              max = Math.max(
                Math.abs(latLngToLayerPoint.x - pointLayerPoint.x),
                Math.abs(latLngToLayerPoint.y - pointLayerPoint.y)
              )
            }
            if (max >= 20 || j === 0 || j === points.length - 1) {
              this._areaLayers.push(
                this._addOneMarker(point.name, latLng, boundary0, sty)
              )
            }
          }
        }
        pointsList.push(latLng)
      }

      if (pointsList.length > 0) {
        if (
          !(
            this._data[i].options &&
            this._data[i].options.minZoom &&
            this._map!.getZoom() < this._data[i].options.minZoom
          )
        ) {
          const options: BaselineOptions = {
            ...defaultOption,
            ...this._data[i].options,
          }
          CommUtils.isEmpty(options.color) && (options.color = colors)
          options.data = this._data[i]

          const polyline = L.polyline(pointsList, options)
          polyline.addTo(this._map!)

          if (CommUtils.isEmpty(this._data[i].name)) {
            polyline.bindPopup(
              () => {
                const html = `
                <div class="box">
                  <div class="title">${this._data[i].name}</div>
                </div>
              `
                return html
              },
              {
                className: 'area-popup',
                closeButton: false,
              }
            )

            polyline.on('mousemove', (e) => {
              e.target.openPopup(e.latlng)
            })

            polyline.on('mouseout', (e) => {
              e.target.closePopup()
            })
          }

          this._areaLayers.push(polyline)
        }
      }
    }

    return this
  }

  /**
   * 添加单个标记
   * @param text 显示文本
   * @param latlng 经纬度
   * @param iconUrl 图标URL
   * @param className 自定义class
   */
  _addOneMarker(
    text: string,
    latlng: L.LatLngLiteral | L.LatLng,
    iconUrl: string,
    className: string
  ): L.Marker {
    const markerOption: L.MarkerOptions = {
      icon: L.icon({
        iconUrl,
        iconSize: [8, 16],
        iconAnchor: [4, 16],
      }),
    }

    const marker = L.marker(latlng, markerOption)

    const textLength = CommUtils.calcStrLength(this._map!, text)
    const popupOffset: [number, number] = [
      Math.max(50, textLength) / 2 + 10,
      10,
    ]
    const popupOptions: BaselinePopupOptions = {
      className,
      closeButton: false,
      offset: popupOffset,
    }

    marker.bindPopup(() => {
      const html = `
        <div class="box">
          <div class="title">${text}</div>
        </div>
      `
      return html
    }, popupOptions)

    marker.on('mousemove', (e) => {
      // marker, marker.getLatLng()
      e.target.openPopup()
    })

    marker.addTo(this._map!)

    return marker
  }
}

const baselinesAtSeaSymbol = (
  map: MyMap,
  options: Partial<AreaViewBaseOptions>
) => new BaselinesAtSeaSymbol(map, options)

export { BaselinesAtSeaSymbol, baselinesAtSeaSymbol }
